/**
 * Board grouping and the summary tiles.
 *
 * These are presentation groupings over authoritative backend values. They may
 * decide which column a deal is DISPLAYED in; they may never decide what a deal
 * IS. Every membership test reads a field the backend returned.
 *
 * The "In pipeline" tile is an explicitly approved presentation grouping
 * (Mike, 2026-08-05): it groups backend stages for display and infers nothing.
 * It is declared here, once, and nowhere else.
 */
import type { Recommendation, Stage } from '../../api/types'

/** Column order for the stage board. Most advanced first, as today. */
export const STAGE_COLUMNS: ReadonlyArray<{ stage: Stage; label: string }> = [
  { stage: 'qualified', label: 'Qualified' },
  { stage: 'negotiation', label: 'Negotiation' },
  { stage: 'proposal', label: 'Proposal' },
  { stage: 'active_scoping', label: 'Active scoping' },
  { stage: 'early_exploration', label: 'Early exploration' },
  { stage: 'blocked', label: 'Blocked' },
  { stage: 'no_opportunity', label: 'No opportunity' },
]

/**
 * THE approved "In pipeline" grouping. Live opportunities advancing toward
 * qualified. Declared once; imported wherever the tile is rendered.
 */
export const IN_PIPELINE_STAGES: ReadonlySet<Stage> = new Set<Stage>([
  'early_exploration',
  'active_scoping',
  'proposal',
  'negotiation',
])

/** Decision-board columns. `null` = no decision recorded yet. */
export const DECISION_COLUMNS: ReadonlyArray<{
  decision: Recommendation | null
  label: string
  accent: string
}> = [
  { decision: null, label: 'Action required', accent: 'var(--dp-ink-3)' },
  { decision: 'keep_investing', label: 'Keep investing', accent: 'var(--dp-keep)' },
  { decision: 'pause', label: 'Pause', accent: 'var(--dp-pause)' },
  { decision: 'stop_investing', label: 'Stop investing', accent: 'var(--dp-stop)' },
]

export function isInPipeline(stage: Stage): boolean {
  return IN_PIPELINE_STAGES.has(stage)
}

/**
 * The four tiles, from the server's counts.
 *
 * The counts arrive keyed by the backend's own stages. Deciding that four of
 * those stages are "in pipeline" is a presentation grouping and stays here,
 * declared once — the backend reports stages and names no group.
 *
 * They are counted over the whole filtered set, not over the cards on screen.
 * Under paging those are different numbers, and the tile that counted the page
 * would fall every time a column was collapsed.
 */
export function summaryCounts(
  countsByStage: Partial<Record<Stage, number>>,
  reEvaluationCount: number,
  activeJobCount: number,
) {
  let inPipeline = 0
  for (const stage of IN_PIPELINE_STAGES) inPipeline += countsByStage[stage] ?? 0
  return {
    qualified: countsByStage.qualified ?? 0,
    inPipeline,
    needsReEvaluation: reEvaluationCount,
    activeAnalyses: activeJobCount,
  }
}
